import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, BadgeCheck, Flag, Users, ReceiptText, ArrowLeft, Share2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const setMeta = (property, content) => {
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

export default function ReviewerProfilePage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/reviewers/${id}`)
      .then(({ data }) => {
        setData(data);
        const title = `${data.reviewer.name} on ProofBuy — warned ${data.stats.buyers_warned} buyers`;
        document.title = title;
        setMeta("og:title", title);
        setMeta("og:description", `${data.stats.live_receipts} verified receipts live. Every one backed by payment + product proof.`);
        setMeta("og:url", window.location.href);
      })
      .catch(() => setError(true));
  }, [id]);

  const share = async () => {
    const text = `${data.reviewer.name} has warned ${data.stats.buyers_warned} buyers on ProofBuy — real receipts, not opinions. ${window.location.href}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {}
  };

  return (
    <div className="min-h-screen bg-paper">
      <header className="backdrop-blur-xl bg-white/70 border-b border-white/40">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="profile-home-link" className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-lg bg-trust flex items-center justify-center">
              <ShieldCheck size={16} className="text-white" />
            </span>
            <span className="font-display font-bold text-lg tracking-tight">ProofBuy</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-trust transition-colors duration-200">
            <ArrowLeft size={15} /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-14">
        {error ? (
          <div data-testid="profile-not-found" className="rounded-2xl bg-white border border-black/5 p-12 text-center">
            <p className="font-display font-semibold text-xl">Reviewer not found.</p>
            <p className="mt-2 text-sm text-gray-500">This profile doesn't exist or isn't public.</p>
          </div>
        ) : !data ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : (
          <>
            <div className="flex items-center gap-5">
              <span className="w-16 h-16 rounded-2xl bg-trust text-white font-display font-bold text-2xl flex items-center justify-center">
                {data.reviewer.name.slice(0, 1).toUpperCase()}
              </span>
              <div>
                <h1 data-testid="profile-name" className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                  {data.reviewer.name}
                </h1>
                <p className="mt-1 text-sm text-gray-500">Verified reviewer on ProofBuy</p>
              </div>
              <button
                data-testid="profile-share-button"
                onClick={share}
                className="ml-auto inline-flex items-center gap-2 rounded-full bg-trust text-white px-5 py-2.5 text-xs font-semibold transition-colors duration-200 hover:bg-trust-dark"
              >
                {copied ? <Check size={14} /> : <Share2 size={14} />}
                {copied ? "Copied — paste it anywhere" : "Share this impact"}
              </button>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
              <div data-testid="profile-stat-live" className="rounded-2xl bg-white border border-black/5 p-5">
                <ReceiptText size={18} className="text-trust" />
                <p className="mt-3 font-display text-2xl font-bold">{data.stats.live_receipts}</p>
                <p className="text-xs text-gray-500 mt-0.5">receipts live</p>
              </div>
              <div data-testid="profile-stat-warned" className="rounded-2xl bg-white border border-black/5 p-5">
                <Users size={18} className="text-verified" />
                <p className="mt-3 font-display text-2xl font-bold">{data.stats.buyers_warned}</p>
                <p className="text-xs text-gray-500 mt-0.5">buyers warned</p>
              </div>
            </div>

            <h2 className="mt-12 font-display font-semibold text-xl">Published receipts</h2>
            {data.receipts.length === 0 ? (
              <p className="mt-4 text-sm text-gray-500">Nothing published yet — receipts appear here after verification.</p>
            ) : (
              <div className="mt-5 grid sm:grid-cols-2 gap-5">
                {data.receipts.map((r, i) => (
                  <article key={r.id} data-testid={`profile-receipt-${i}`} className="rounded-2xl bg-white border border-black/5 shadow-sm p-6">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-display font-semibold text-base tracking-tight">{r.product_name}</h3>
                        <p className="mt-0.5 text-sm text-gray-500">{r.seller_name}</p>
                      </div>
                      <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-verified-soft text-verified text-[10px] font-bold px-2.5 py-1 uppercase tracking-wider">
                        <BadgeCheck size={11} /> Verified
                      </span>
                    </div>
                    <div className="receipt-dash my-3" />
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between gap-3"><dt className="text-gray-500">Paid</dt><dd className="font-semibold">{r.price_paid}</dd></div>
                      <div className="flex justify-between gap-3"><dt className="text-gray-500 shrink-0">Received</dt><dd className="font-semibold text-right text-warn">{r.received}</dd></div>
                    </dl>
                    {r.resell_rights === "yes" && (
                      <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-gold-soft text-yellow-700 text-[10px] font-bold px-2.5 py-1 uppercase tracking-wider">
                        <Flag size={11} /> Resell-rights pack
                      </span>
                    )}
                  </article>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
